import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, CheckCircle, Clock, Plane, Calendar, Shield } from 'lucide-react';
import PublicHeader from '../components/PublicHeader';

export default function MedicalVisa() {
  const navigate = useNavigate();

  const visaSteps = [
    {
      step: 1,
      title: 'Apply Online',
      description: 'Complete the online visa application form',
      icon: <FileText className="w-8 h-8" />,
      color: 'from-blue-500 to-blue-600'
    },
    {
      step: 2,
      title: 'Upload Documents',
      description: 'Submit required medical and travel documents',
      icon: <Shield className="w-8 h-8" />,
      color: 'from-purple-500 to-purple-600'
    },
    {
      step: 3,
      title: 'Get Approval',
      description: 'Receive e-visa approval within 3-5 business days',
      icon: <CheckCircle className="w-8 h-8" />,
      color: 'from-green-500 to-green-600'
    },
    {
      step: 4,
      title: 'Travel to India',
      description: 'Arrive in India with your e-visa confirmation',
      icon: <Plane className="w-8 h-8" />,
      color: 'from-orange-500 to-orange-600'
    }
  ];

  const requiredDocuments = [
    'Valid passport (minimum 6 months validity)',
    'Recent passport-size photograph',
    'Letter from recognized hospital in India',
    'Medical records from your home country',
    'Bank statement or financial proof',
    'Return flight tickets (or proof of booking)',
    'Visa application fee ($80)'
  ];

  const visaDetails = [
    {
      title: 'Visa Duration',
      value: 'Up to 60 days',
      icon: <Calendar className="w-6 h-6" />
    },
    {
      title: 'Processing Time',
      value: '3-5 business days',
      icon: <Clock className="w-6 h-6" />
    },
    {
      title: 'Entry Type',
      value: 'Triple entry allowed',
      icon: <Plane className="w-6 h-6" />
    },
    {
      title: 'Extension',
      value: 'Can be extended',
      icon: <FileText className="w-6 h-6" />
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <PublicHeader currentPage="medical-visa" />

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-orange-50 via-red-50 to-yellow-50">
        <div className="mx-auto max-w-7xl text-center">
          <h1 className="font-heading text-5xl font-bold text-gray-900 mb-6">
            Medical Visa for <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">India</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            We'll guide you through the entire medical visa process for your treatment in India.
          </p>
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-white rounded-full border border-orange-200">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="font-semibold text-gray-900">Fast Processing</span>
            <span className="text-gray-600">•</span>
            <span className="font-semibold text-gray-900">3-5 Days</span>
            <span className="text-gray-600">•</span>
            <span className="font-semibold text-gray-900">E-Visa Available</span>
          </div>
        </div>
      </section>

      {/* Visa Process Steps */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-4xl font-bold text-gray-900 mb-4">
              Simple Visa Process
            </h2>
            <p className="text-gray-600">Get your medical visa in 4 easy steps</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {visaSteps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="relative mb-6">
                  <div className={`w-20 h-20 mx-auto rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center text-white shadow-lg`}>
                    {step.icon}
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                    {step.step}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-600 text-sm">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Visa Details */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-orange-50 to-red-50">
        <div className="mx-auto max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-4xl font-bold text-gray-900 mb-4">
              Visa Information
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {visaDetails.map((detail, index) => (
              <div key={index} className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 mx-auto mb-4 bg-orange-100 rounded-full flex items-center justify-center text-orange-600">
                  {detail.icon}
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">{detail.title}</h3>
                <p className="text-orange-600 font-bold">{detail.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Required Documents */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-4xl font-bold text-gray-900 mb-4">
              Required Documents
            </h2>
            <p className="text-gray-600">Make sure you have these documents ready for your application</p>
          </div>
          
          <div className="bg-white rounded-xl border border-gray-200 p-8">
            <div className="space-y-4">
              {requiredDocuments.map((doc, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  </div>
                  <p className="text-gray-700">{doc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* We Help Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-orange-50 to-red-50">
        <div className="mx-auto max-w-4xl text-center">
          <div className="text-5xl mb-6">🤝</div>
          <h2 className="font-heading text-4xl font-bold text-gray-900 mb-4">
            We're Here to Help
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Our case managers will assist you with your entire visa application process, document preparation, and travel arrangements.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6">
              <div className="text-3xl mb-3">📝</div>
              <h3 className="font-bold text-gray-900 mb-2">Document Guidance</h3>
              <p className="text-sm text-gray-600">Help with preparing all required documents</p>
            </div>
            <div className="bg-white rounded-xl p-6">
              <div className="text-3xl mb-3">✅</div>
              <h3 className="font-bold text-gray-900 mb-2">Application Support</h3>
              <p className="text-sm text-gray-600">Assistance with filling out visa forms</p>
            </div>
            <div className="bg-white rounded-xl p-6">
              <div className="text-3xl mb-3">📞</div>
              <h3 className="font-bold text-gray-900 mb-2">24/7 Support</h3>
              <p className="text-sm text-gray-600">Available to answer all your questions</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-orange-500 to-red-600 text-white">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="font-heading text-4xl font-bold mb-6">
            Ready to Start Your Visa Application?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Register now and our team will guide you through the entire process
          </p>
          <button
            onClick={() => navigate('/register')}
            className="px-8 py-4 bg-white text-orange-600 rounded-xl font-semibold text-lg hover:shadow-xl transition-all transform hover:scale-105"
          >
            Get Visa Assistance →
          </button>
        </div>
      </section>
    </div>
  );
}
